(() => {
  if (window.__bixdoA30RelaunchLoaded) return;
  window.__bixdoA30RelaunchLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealSelector = [
    '.bixdo-a30-story__heading',
    '.bixdo-a30-story__body',
    '.bixdo-a30-story__lead-media',
    '.bixdo-a30-story__clean-media',
    '.bixdo-a30-story__comfort-media',
    '.bixdo-a30-story__fresh-diagram',
    '.bixdo-a30-story__material-list',
    '.bixdo-a30-story__step',
    '.bixdo-a30-story__fine-print',
    '.bixdo-a30-story__compact-slide',
    '.bixdo-a30-story__clean-benefit',
    '.bixdo-a30-story__clean-spec',
    '.bixdo-a30-story__tip-card'
  ].join(',');

  const revealObserver = !reducedMotion.matches && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;

  const initializeReveals = (scope = document) => {
    const modules = scope.matches?.('[data-a30-story-section]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-a30-story-section]'));

    modules.forEach((module) => {
      Array.from(module.querySelectorAll(revealSelector)).forEach((element, index) => {
        if (element.dataset.a30RevealInitialized === 'true') return;
        element.dataset.a30RevealInitialized = 'true';
        element.classList.add('bixdo-a30-reveal');
        element.style.setProperty('--a30-reveal-delay', `${Math.min(index % 4, 3) * 55}ms`);

        if (element.matches('.bixdo-a30-story__lead-media, .bixdo-a30-story__clean-media, .bixdo-a30-story__comfort-media, .bixdo-a30-story__fresh-diagram, .bixdo-a30-story__step, .bixdo-a30-story__compact-slide')) {
          element.classList.add('bixdo-a30-reveal--media');
        }

        if (revealObserver) revealObserver.observe(element);
        else element.classList.add('is-visible');
      });
    });
  };

  const initializeCompactTracks = (scope = document) => {
    const tracks = scope.matches?.('[data-a30-compact-track]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-a30-compact-track]'));

    tracks.forEach((trackElement) => {
      if (trackElement.dataset.a30CompactInitialized === 'true') return;
      const slides = Array.from(trackElement.querySelectorAll('[data-a30-compact-slide]'));
      const current = trackElement.closest('[data-a30-section="compact"]')?.querySelector('[data-a30-compact-current]');
      if (!current || slides.length < 2) return;

      let frame = 0;
      const update = () => {
        frame = 0;
        const left = trackElement.getBoundingClientRect().left;
        const index = slides.reduce((closest, slide, slideIndex) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - left);
          return distance < closest.distance ? { index: slideIndex, distance } : closest;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
        current.textContent = String(index + 1);
      };

      trackElement.dataset.a30CompactInitialized = 'true';
      trackElement.addEventListener('scroll', () => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      }, { passive: true });
      update();
    });
  };

  const initializeMediaSwitchers = (scope = document) => {
    const switchers = scope.matches?.('[data-a30-media-switcher]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-a30-media-switcher]'));

    switchers.forEach((switcher) => {
      if (switcher.dataset.a30SwitcherInitialized === 'true') return;
      const triggers = Array.from(switcher.querySelectorAll('[data-a30-media-trigger]'));
      const slides = Array.from(switcher.querySelectorAll('[data-a30-media-slide]'));
      if (!triggers.length || !slides.length) return;

      let activeIndex = 0;
      let timer = 0;
      const interval = Math.max(3000, Number(switcher.dataset.a30Interval) || 5000);
      const autoplay = switcher.dataset.a30Autoplay === 'true' && !reducedMotion.matches && triggers.length > 1;

      const show = (nextIndex, focusTrigger = false) => {
        activeIndex = (nextIndex + triggers.length) % triggers.length;
        triggers.forEach((trigger, index) => {
          const selected = index === activeIndex;
          trigger.classList.toggle('is-active', selected);
          trigger.setAttribute('aria-selected', String(selected));
          trigger.tabIndex = selected ? 0 : -1;
          if (selected && focusTrigger) trigger.focus({ preventScroll: true });
        });
        slides.forEach((slide, index) => {
          const selected = index === activeIndex;
          slide.classList.toggle('is-active', selected);
          slide.hidden = !selected;
          slide.querySelectorAll('video').forEach((video) => {
            if (selected) {
              if (video.autoplay) video.play().catch(() => {});
            } else {
              video.pause();
              video.currentTime = 0;
            }
          });
        });
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = 0;
      };
      const start = () => {
        stop();
        if (autoplay) timer = window.setInterval(() => show(activeIndex + 1), interval);
      };

      triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => {
          show(index);
          start();
        });
        trigger.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') show(0, true);
          else if (event.key === 'End') show(triggers.length - 1, true);
          else show(activeIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
          start();
        });
      });

      switcher.addEventListener('mouseenter', stop);
      switcher.addEventListener('mouseleave', start);
      switcher.addEventListener('focusin', stop);
      switcher.addEventListener('focusout', (event) => {
        if (!switcher.contains(event.relatedTarget)) start();
      });

      switcher.dataset.a30SwitcherInitialized = 'true';
      show(0);
      start();
    });
  };

  const track = (element, action) => {
    const section = element.closest('[data-a30-section]')?.dataset.a30Section || 'unknown';
    const payload = {
      event: 'bixdo_a30_interaction',
      a30_action: action || element.dataset.bixdoTrack || 'interaction',
      a30_section: section,
      a30_label: element.dataset.bixdoTrackLabel || element.textContent.trim().replace(/\s+/g, ' ').slice(0, 80)
    };
    document.dispatchEvent(new CustomEvent('bixdo:track', { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  };

  document.addEventListener('click', (event) => {
    const tracked = event.target.closest('[data-bixdo-track]');
    if (tracked) track(tracked);
  });

  const initializeAll = (scope = document) => {
    initializeReveals(scope);
    initializeCompactTracks(scope);
    initializeMediaSwitchers(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
